import { useQuery } from '@tanstack/react-query';
import { supabaseHarvest } from '@/lib/supabase';
import { getTimeframe, type TimeframeKey } from '@/config/timeframes';
import { bucketLabel } from '@/lib/timeBucket';
import { parseHarvestJob, type HarvestProvider } from '@/lib/utils';
import type { ManifestEntry, ManifestSummaryRow } from '@/types/harvest';
import type { HangfireJobRow } from '@/types/hangfire';

export interface HarvestFailure {
  id: number;
  provider: HarvestProvider;
  label: string;
  failedAt: string;
  // Exception detail from the Failed state's `data` (harvest.state), so
  // operators can diagnose without DB access (#62). Null when the state row
  // isn't readable.
  errorType?: string | null;
  errorMessage?: string | null;
}

/**
 * A Failed state caused purely by the concurrency guards (#70/#72): when a
 * duplicate recurring fire can't acquire the per-job/per-arg distributed lock,
 * `DisableConcurrentExecution(0)` throws and the job is marked Failed. That's
 * the intended "fail fast, don't stack" behavior, NOT a harvest failure — so we
 * exclude it from the failures panel to keep it actionable.
 */
function isBenignLockTimeout(errorType?: string | null, errorMessage?: string | null): boolean {
  return (
    (errorType?.includes('DistributedLockException') ?? false) ||
    (errorMessage?.includes('Could not place a lock') ?? false)
  );
}

export interface ManifestFilters {
  provider?: string;
  sport?: string;
  endpointType?: string;
  limit?: number;
}

export function useManifestSummary() {
  return useQuery({
    queryKey: ['manifest-summary'],
    queryFn: async (): Promise<ManifestSummaryRow[]> => {
      const { data, error } = await supabaseHarvest
        .from('v_manifest_summary')
        .select('*')
        .order('object_count', { ascending: false });
      if (error) throw error;
      return (data ?? []) as ManifestSummaryRow[];
    },
  });
}

/**
 * Failed harvest/sync jobs from the last 24h, read from Hangfire's
 * `harvest.job` table (same access pattern as `useRecentJobs` in
 * useHangfire.ts). Rows that don't parse to a known Genius/Presto harvest
 * or sync job (`parseHarvestJob` returns null) are dropped so the card
 * stays the actionable subset — not a raw Hangfire job list.
 */
export function useRecentHarvestFailures(limit = 200) {
  return useQuery({
    queryKey: ['harvest-failures', limit],
    queryFn: async (): Promise<{ failures: HarvestFailure[]; truncated: boolean }> => {
      const since = new Date(Date.now() - 24 * 3600_000).toISOString();
      const { data, error } = await supabaseHarvest
        .from('job')
        .select('id, statename, invocationdata, createdat')
        .eq('statename', 'Failed')
        .gte('createdat', since)
        .order('createdat', { ascending: false })
        .limit(limit);
      if (error) throw error;
      // The `.limit()` is applied DB-side across ALL failed jobs before the
      // harvest-only filter runs client-side, so genuine harvest failures can
      // be dropped if there are many non-harvest failures in the window.
      // Surface `truncated` (== the raw row count hit the limit) so the UI can
      // warn — mirroring `useJobStateCounts`'s `{ counts, truncated }` shape.
      const rows = (data ?? []) as HangfireJobRow[];
      const harvestFailures = rows
        .map((r) => ({ raw: r, parsed: parseHarvestJob(r.invocationdata) }))
        .filter((x): x is { raw: HangfireJobRow; parsed: { provider: HarvestProvider; label: string } } =>
          x.parsed !== null,
        )
        .map((x) => ({
          id: x.raw.id,
          provider: x.parsed.provider,
          label: x.parsed.label,
          failedAt: x.raw.createdat,
        }));

      const withErrors = await attachFailureDetails(harvestFailures);
      // Drop the benign concurrency-guard lock-timeouts so the panel only shows
      // real failures (HTTP/DB/sync errors operators can act on).
      const failures = withErrors.filter(
        (f) => !isBenignLockTimeout(f.errorType, f.errorMessage),
      );
      return { failures, truncated: rows.length >= limit };
    },
  });
}

/**
 * Merge ExceptionType/ExceptionMessage onto failed jobs from the Hangfire
 * `state` table's Failed-state `data`. No FK from job.stateid → state.id, so
 * fetch by jobid and join client-side. Best-effort: if `state` isn't readable
 * the failures still render without detail.
 */
async function attachFailureDetails(failures: HarvestFailure[]): Promise<HarvestFailure[]> {
  if (failures.length === 0) return failures;
  const { data, error } = await supabaseHarvest
    .from('state')
    .select('jobid, data')
    .eq('name', 'Failed')
    .in('jobid', failures.map((f) => f.id));
  if (error || !data) return failures;

  const byJob = new Map<number, { type?: string | null; message?: string | null }>();
  for (const row of data as { jobid: number; data: { ExceptionType?: string; ExceptionMessage?: string } | null }[]) {
    // A job can have multiple Failed transitions (retries); the query returns
    // them in arbitrary order, so keep the first seen — they describe the same
    // recurring fire closely enough for the panel.
    if (!byJob.has(row.jobid)) {
      byJob.set(row.jobid, { type: row.data?.ExceptionType, message: row.data?.ExceptionMessage });
    }
  }

  return failures.map((f) => {
    const detail = byJob.get(f.id);
    return detail ? { ...f, errorType: detail.type ?? null, errorMessage: detail.message ?? null } : f;
  });
}

export function useManifest(filters: ManifestFilters = {}) {
  return useQuery({
    queryKey: ['manifest', filters],
    queryFn: async (): Promise<ManifestEntry[]> => {
      let q = supabaseHarvest
        .from('manifest')
        .select('*')
        .order('collected_at', { ascending: false })
        .limit(filters.limit ?? 100);
      if (filters.provider) q = q.eq('provider', filters.provider);
      if (filters.sport) q = q.eq('sport', filters.sport);
      if (filters.endpointType) q = q.eq('endpoint_type', filters.endpointType);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as ManifestEntry[];
    },
  });
}

/**
 * Throughput of manifest writes over the selected timeframe, bucketed server-side
 * via the harvest.fn_manifest_timeseries RPC. Returns a gap-filled, fixed-length
 * bucket series so every window renders the correct number of x-axis points
 * including the current period (replaces the raw-row pull that was silently
 * truncated by PostgREST's max-rows cap).
 */
export function useManifestThroughput(timeframeKey: TimeframeKey) {
  const tf = getTimeframe(timeframeKey);
  return useQuery({
    queryKey: ['manifest-throughput', tf.key],
    staleTime: 60_000,
    queryFn: async (): Promise<{ buckets: { bucket: string; count: number }[]; truncated: boolean }> => {
      const { data, error } = await supabaseHarvest.rpc('fn_manifest_timeseries', {
        p_hours: tf.hours,
        p_bucket_seconds: Math.round(tf.bucketMs / 1000),
      });
      if (error) throw error;
      const buckets = ((data ?? []) as { bucket_start: string; object_count: number }[]).map((r) => ({
        bucket: bucketLabel(new Date(r.bucket_start).getTime(), tf.bucketMs),
        count: Number(r.object_count),
      }));
      return { buckets, truncated: false };
    },
  });
}

